/**
 * ExaminerCard - Display examiner information
 * Nordic Sanctuary design: Professional, trustworthy, approachable
 */

import { useTranslation } from "react-i18next";
import { User, Stethoscope } from "lucide-react";

interface ExaminerCardProps {
  name: string;
  title?: string;
  photoUrl?: string;
}

export function ExaminerCard({ name, title, photoUrl }: ExaminerCardProps) {
  const { t } = useTranslation("appointment");

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--sage-50)] to-[var(--sage-100)] p-5">
      {/* Decorative background element */}
      <div
        className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-[var(--sage-200)] opacity-40"
        aria-hidden="true"
      />

      <div className="relative flex items-center gap-4">
        {/* Avatar */}
        <div className="relative">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--sage-400)] to-[var(--sage-500)] flex items-center justify-center overflow-hidden shadow-lg shadow-[var(--sage-300)]/30">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={name}
                className="w-full h-full object-cover"
              />
            ) : (
              <User className="w-8 h-8 text-white" aria-hidden="true" />
            )}
          </div>
          {/* Online indicator */}
          <div
            className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow-sm"
            aria-hidden="true"
          >
            <Stethoscope className="w-3 h-3 text-[var(--sage-600)]" />
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-[var(--sage-600)] mb-0.5">
            {t("details.examiner")}
          </p>
          <p className="text-lg font-bold text-[var(--stone-800)] truncate">
            {name}
          </p>
          {title && (
            <p className="text-sm text-[var(--stone-500)] truncate">{title}</p>
          )}
        </div>
      </div>
    </div>
  );
}
